import React, { useLayoutEffect, useRef, forwardRef } from "react";
import { absUrl, packTreatment, readabilityRules } from "./MoodCard";
import { getClusterStyle } from "../lib/moodLanes";

/**
 * ExportCard — pixel-locked rendering for PNG export.
 *
 * Why a separate component (and not <MoodCard size="full">):
 *   • The in-app MoodCard uses responsive Tailwind classes (text-xl sm:text-2xl).
 *     Those resolve via root font-size. Two browsers / two zoom levels can render
 *     the same card at slightly different sizes — bad for shareables.
 *   • ExportCard uses ONLY absolute px values. Same source card → same pixels,
 *     every time, on every device.
 *   • Hard safe-area + auto-shrink-to-fit guarantees text never overflows the
 *     bottom edge no matter how long the LLM gets.
 *
 * Props
 *   • card    — required, same shape as MoodCard's card prop
 *   • width   — canvas width in pixels (default 1080)
 *   • height  — canvas height in pixels (default 1920)
 *   • showWatermark — bool, default true
 */
const SAFE_INSET_RATIO = 0.067;            // 7% of width → safe text gutter
const FOOTER_HEIGHT_RATIO = 0.085;         // reserved for caption + brand strip
const TITLE_TO_BODY_GAP = 0.025;           // gap between title & body, as ratio of height

// Starting body font-size per style, expressed as ratio of canvas WIDTH so it
// scales with non-1080 canvases (e.g. 1200×675 for X cards).
//
// These match MoodCard.jsx full-size typography (iter27 fix):
//   relatable text-[104px] → 9.6%
//   poetic    text-[96px]  → 8.9%
//   bold      text-[124px] → 11.5%
// The auto-shrink-to-fit safety net handles long text.
const INITIAL_BODY_FS_RATIO = {
    relatable: 0.096,
    poetic:    0.089,
    bold:      0.115,
};
const TITLE_FS_RATIO = 0.063;              // ~68px on 1080 canvas — matches MoodCard text-[68px]
const LABEL_FS_RATIO = 0.0185;             // ~20px on 1080 canvas
const WATERMARK_FS_RATIO = 0.078;          // ~84px on 1080 canvas
const CAPTION_FS_RATIO = 0.0175;
const MIN_BODY_FS_RATIO = 0.052;           // floor — never below ~56px on 1080 (matches MoodCard floor)

const ExportCard = forwardRef(({ card, width = 1080, height = 1920, showWatermark = true, lane = null, temperature = null, variant = null }, ref) => {
    const bodyContainerRef = useRef(null);
    const bodyTextRef = useRef(null);
    const titleTextRef = useRef(null);

    // Phase 3·b — cluster-aware tweaks for the export PNG so shared cards
    // carry the same subtle emotional adaptation as the in-app preview.
    const cluster = getClusterStyle(lane, temperature);

    // Derive style-dependent metrics. Safe to compute even when card is missing —
    // hooks must always run in the same order, regardless of early returns.
    const style = card?.style || "relatable";
    const inset = Math.round(width * SAFE_INSET_RATIO);
    const initialBodyFs = Math.round(width * (INITIAL_BODY_FS_RATIO[style] || 0.096));
    const minBodyFs = Math.round(width * MIN_BODY_FS_RATIO);

    // Auto-shrink-to-fit: imperatively step font-size down until body fits inside its box.
    // No state used → no extra renders. Runs on every render (cheap).
    useLayoutEffect(() => {
        if (!card) return;
        const container = bodyContainerRef.current;
        const text = bodyTextRef.current;
        if (container && text) {
            let fs = initialBodyFs;
            text.style.fontSize = `${fs}px`;
            for (let i = 0; i < 12; i += 1) {
                if (text.scrollHeight <= container.clientHeight) break;
                if (fs <= minBodyFs) break;
                fs -= Math.max(2, Math.round(width * 0.004));
                text.style.fontSize = `${fs}px`;
            }
        }
        // Title shrink — keep hashtag on ONE line. Now allows wrap as a
        // safety net AND walks down by line-count (more robust against
        // sub-pixel measurement drift than scrollWidth checks).
        const title = titleTextRef.current;
        if (title) {
            const initTitleFs = Math.round(width * TITLE_FS_RATIO);
            let tfs = initTitleFs;
            title.style.fontSize = `${tfs}px`;
            const titleMin = Math.round(width * 0.024);  // ~26px floor at 1080
            for (let i = 0; i < 22; i += 1) {
                const lh = parseFloat(window.getComputedStyle(title).lineHeight) || tfs * 1.1;
                const lines = Math.round(title.scrollHeight / lh);
                if (lines <= 1) break;
                if (tfs <= titleMin) break;
                tfs -= Math.max(2, Math.round(width * 0.003));
                title.style.fontSize = `${tfs}px`;
            }
        }
    }, [card, width, height, initialBodyFs, minBodyFs]);

    if (!card) return null;

    const pack = card.pack || "cinematic";
    const t = packTreatment(pack, card.style);
    const r = readabilityRules(t.readability, t.textColor);

    const footerH = Math.round(height * FOOTER_HEIGHT_RATIO);
    const titleGap = Math.round(height * TITLE_TO_BODY_GAP);
    const titleFs = Math.round(width * TITLE_FS_RATIO);
    const labelFs = Math.round(width * LABEL_FS_RATIO);
    const watermarkFs = Math.round(width * WATERMARK_FS_RATIO);
    const captionFs = Math.round(width * CAPTION_FS_RATIO);

    return (
        <div
            ref={ref}
            data-testid={`export-card-${card.style}`}
            style={{
                position: "relative",
                width: `${width}px`,
                height: `${height}px`,
                overflow: "hidden",
                background: "#050507",
                fontFamily: "'Outfit', 'Helvetica Neue', Arial, sans-serif",
                color: t.textColor,
                // Pure pixel sizes — DO NOT use rem/em here.
                fontSize: "16px",  // baseline for any unscoped relative units
                lineHeight: 1.2,
            }}
        >
            {/* Background — image OR CSS gradient for CSS-only packs.
                When a `variant` filter is supplied, applied to this layer
                ONLY so text/accents/watermark stay legible. */}
            {t.cssBg ? (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background: t.cssBg,
                        filter: variant?.filter && variant.filter !== "none" ? variant.filter : undefined,
                    }}
                />
            ) : (
                <img
                    src={absUrl(card.background_url)}
                    alt=""
                    crossOrigin="anonymous"
                    draggable={false}
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: variant?.filter && variant.filter !== "none" ? variant.filter : undefined,
                    }}
                />
            )}
            {/* Vignette */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background: `radial-gradient(120% 80% at 50% 50%, transparent 30%, rgba(0,0,0,${t.vignetteOpacity}) 100%)`,
                }}
            />
            {/* Readability scrim */}
            {r.scrim && <div style={{ position: "absolute", inset: 0, background: r.scrim, pointerEvents: "none" }} />}

            {/* Material overlays — premium packs */}
            {t.materialLayer === "cosmic" && (
                <>
                    <div className="mc-pack-cosmic-shimmer" style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
                    <div className="mc-pack-cosmic-stars" style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
                </>
            )}
            {t.materialLayer === "romantic" && (
                <div className="mc-pack-romantic-glow" style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
            )}
            {t.materialLayer === "dark" && (
                <div className="mc-pack-dark-specular" style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
            )}

            {/* Phase 3·b — cluster tint overlay (export parity with in-app preview). */}
            {cluster?.tintRGBA && (
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        background: `radial-gradient(140% 100% at 50% 100%, ${cluster.tintRGBA} 0%, transparent 70%)`,
                        mixBlendMode: "screen",
                    }}
                />
            )}

            {/* Watermark — push toward bottom-left so it never overlaps centered text */}
            {showWatermark && (
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: `${Math.round(height * 0.16)}px`,
                        display: "flex",
                        justifyContent: "center",
                        pointerEvents: "none",
                        transform: "rotate(-22deg)",
                        opacity: 0.14,
                    }}
                >
                    <div
                        style={{
                            fontFamily: "'Plantin', 'Georgia', serif",
                            fontStyle: "italic",
                            fontSize: `${watermarkFs}px`,
                            letterSpacing: "0.05em",
                            color: t.watermarkColor,
                            whiteSpace: "nowrap",
                        }}
                    >
                        Made with MoodCard · Made with MoodCard
                    </div>
                </div>
            )}

            {/* Content stack with strict safe area */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    padding: `${inset}px`,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Top brand strip */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: `${labelFs * 1.6}px` }}>
                    <span
                        style={{
                            fontFamily: "'Outfit', sans-serif",
                            textTransform: "uppercase",
                            fontSize: `${labelFs}px`,
                            letterSpacing: "0.4em",
                            color: t.labelColor,
                            opacity: 0.78,
                            textShadow: r.labelShadow || "none",
                        }}
                    >
                        MoodCard
                    </span>
                </div>
                {/* Center stack: title + body container (body container has fixed height for shrink-to-fit) */}
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            maxWidth: `${Math.round(width * 0.94)}px`,
                            padding: `${Math.round(width * 0.022)}px ${Math.round(width * 0.028)}px`,
                            ...(r.glass || {}),
                            borderRadius: r.glass ? `${Math.round(width * 0.017)}px` : 0,
                        }}
                    >
                        {/* Title — auto-shrinks alongside body via useLayoutEffect. */}
                        <div
                            ref={titleTextRef}
                            style={{
                                fontFamily: "'Plantin', 'Georgia', serif",
                                fontStyle: "italic",
                                fontSize: `${titleFs}px`,
                                lineHeight: 1.05,
                                color: t.textColor,
                                textShadow: r.titleShadow || "none",
                                opacity: 0.95,
                                marginBottom: `${Math.round(titleGap * 0.4)}px`,
                                maxWidth: "100%",
                                letterSpacing: "0.02em",
                                overflowWrap: "anywhere",  // safety fallback if shrink hits floor
                                overflow: "hidden",
                            }}
                        >
                            #{(card.title || "").trim().replace(/\s+/g, "_")}
                        </div>
                        {/* MOODCARD watermark stamp — sits between topic and body.
                            Matches MoodCard.jsx preview. 15% opacity wordmark,
                            horizontal, regular sans, generous tracking. Quiet
                            brand presence for screenshots. */}
                        <div
                            style={{
                                fontFamily: "'Outfit', sans-serif",
                                textTransform: "uppercase",
                                fontSize: `${Math.round(width * 0.026)}px`,
                                letterSpacing: "0.42em",
                                color: t.textColor,
                                opacity: 0.15,
                                marginBottom: `${titleGap}px`,
                                lineHeight: 1,
                            }}
                        >
                            moodcard
                        </div>
                        {/* Body container with FIXED height — auto-shrink ensures text fits */}
                        <div
                            ref={bodyContainerRef}
                            style={{
                                width: "100%",
                                // available height = canvas - 2*inset - top label - title - footer reserved
                                height: `${height - 2 * inset - Math.round(labelFs * 1.6) - titleFs - titleGap - footerH - Math.round(height * 0.04)}px`,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                ref={bodyTextRef}
                                style={{
                                    fontFamily: card.style === "bold"
                                        ? "'Outfit', sans-serif"
                                        : "'Plantin', 'Georgia', serif",
                                    fontWeight: card.style === "bold" ? 700 : 400,
                                    fontStyle: card.style === "bold" ? "normal" : "italic",
                                    textTransform: card.style === "bold" ? "uppercase" : "none",
                                    letterSpacing: card.style === "bold" ? "0.02em" : "0",
                                    fontSize: `${initialBodyFs}px`,
                                    lineHeight: cluster?.lineHeight ?? 1.18,
                                    letterSpacing: cluster?.letterSpacing || (card.style === "bold" ? "0.02em" : "0"),
                                    color: t.textColor,
                                    textShadow: r.bodyShadow || "none",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {card.body}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer brand strip */}
                <div
                    style={{
                        height: `${footerH}px`,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: `${Math.round(captionFs * 0.6)}px`,
                    }}
                >
                    {card.caption ? (
                        <div
                            style={{
                                fontFamily: "'Plantin', 'Georgia', serif",
                                fontStyle: "italic",
                                fontSize: `${captionFs}px`,
                                color: t.labelColor,
                                opacity: 0.72,
                                textAlign: "center",
                                maxWidth: `${Math.round(width * 0.78)}px`,
                                lineHeight: 1.35,
                                marginBottom: `${Math.round(captionFs * 0.6)}px`,
                            }}
                        >
                            {(card.caption || "").split("\n")[0]}
                        </div>
                    ) : null}
                    {/* "MADE WITH MOODCARD.APP" — balanced two-word treatment.
                        Both words share font + size + weight so neither
                        dominates the other. Matches MoodCard.jsx preview exactly. */}
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: `${Math.round(labelFs * 0.42)}px` }}>
                        <span
                            style={{
                                fontFamily: "'Outfit', sans-serif",
                                textTransform: "uppercase",
                                fontSize: `${labelFs * 0.92}px`,
                                letterSpacing: "0.32em",
                                color: t.labelColor,
                                opacity: 0.82,
                                fontWeight: 500,
                                lineHeight: 1,
                            }}
                        >
                            MADE WITH
                        </span>
                        <span
                            style={{
                                fontFamily: "'Outfit', sans-serif",
                                textTransform: "uppercase",
                                fontSize: `${labelFs * 0.92}px`,
                                letterSpacing: "0.32em",
                                color: t.labelColor,
                                opacity: 0.82,
                                fontWeight: 500,
                                lineHeight: 1,
                            }}
                        >
                            MOODCARD.APP
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
});

ExportCard.displayName = "ExportCard";
export default ExportCard;
